import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Button } from 'semantic-ui-react';
import { useMutation, gql } from '@apollo/client';

import { authProvider } from '../../auth';
import { PATHS } from '../../paths';
import banner from '../../images/login-banner.png';
import formStyles from '../../components/AuthCard/form.module.css';
import styles from './index.module.css';

/**
 * Public account deletion.
 *
 * Google Play requires two deletion paths for any app with accounts: one inside the app
 * (Settings → Delete account) **and** a web URL that works without installing the app. This
 * is that URL — it must stay reachable without being signed in first, which is why the page
 * does its own login rather than sitting behind the router's auth guard.
 *
 * Styled like `PrivacyPolicy`/`AuthCard` (navy page, white card) rather than bare Semantic UI
 * on the app's themed background — this page has to stay legible on its own regardless of the
 * viewer's light/dark preference, since it isn't part of the authenticated app shell that the
 * theme system is designed around.
 */

const LOGIN = gql`
  mutation Login($userName: String!, $password: String!) {
    login(userName: $userName, password: $password) {
      success
      token
      refreshToken
    }
  }
`;

const DELETE_ACCOUNT = gql`
  mutation DeleteAccount {
    deleteAccount {
      success
      message
    }
  }
`;

export const DeleteAccount = () => {
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deleted, setDeleted] = useState<string | null>(null);

  const [login, { loading: loggingIn }] = useMutation(LOGIN);
  const [deleteAccount, { loading: deleting }] = useMutation(DELETE_ACCOUNT);

  const busy = loggingIn || deleting;
  const canSubmit =
    userName.trim() !== '' && password !== '' && confirmText === 'DELETE' && !busy;

  /**
   * Credentials are always required, even for someone already signed in on this browser.
   * Deletion is irreversible, and this page is a public URL that may well be opened on a
   * shared computer — proving ownership is worth the extra step here.
   */
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);

    try {
      const { data: loginData } = await login({
        variables: { userName: userName.trim(), password },
      });
      const result = loginData?.login;
      if (!result?.success || !result.token) {
        setError("That email or password wasn't right.");
        return;
      }

      // Store the token so Apollo's authLink picks it up for the delete call, which is
      // scoped to the signed-in user server-side.
      await authProvider.signin(result.token, result.refreshToken);

      const { data: deleteData } = await deleteAccount();
      const outcome = deleteData?.deleteAccount;
      if (!outcome?.success) {
        setError(outcome?.message ?? "We couldn't delete that account. Please contact us.");
        return;
      }

      // The account is gone, so the tokens we just stored are dead. Clear them directly
      // rather than via signout(), whose Logout call would now be pointless.
      localStorage.clear();
      setDeleted(outcome.message ?? 'Your account has been deleted.');
    } catch (e: any) {
      setError(e?.message ?? 'Something went wrong. Please try again.');
    }
  };

  if (deleted) {
    return (
      <div className={styles.page}>
        <div className={styles.brandBar}>
          <img src={banner} alt="Joyya" className={styles.brandLogo} />
        </div>
        <div className={styles.content}>
          <div className={styles.doc}>
            <h1 className={styles.docTitle}>Account deleted</h1>
            <p className={styles.docSubtitle}>{deleted}</p>
            <p className={styles.footer}>
              If you have any questions, email us at{' '}
              <a className={styles.contactLink} href="mailto:developers@joyya.com">
                developers@joyya.com
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.brandBar}>
        <img src={banner} alt="Joyya" className={styles.brandLogo} />
      </div>
      <div className={styles.content}>
        <div className={styles.doc}>
          <h1 className={styles.docTitle}>Delete your Joyya account</h1>
          <p className={styles.docSubtitle}>
            This permanently deletes your Joyya account. You'll lose access to your
            organisation's feed, communities and members, on the website and in the mobile
            apps.
          </p>

          <hr className={styles.divider} />

          <h2 className={styles.sectionTitle}>What gets deleted</h2>
          <ul className={styles.list}>
            <li>Your account and sign-in details</li>
            <li>Your profile — name, profile photo, phone number, address and date of birth</li>
            <li>Your membership of any organisation</li>
            <li>Your registered devices, so you stop receiving notifications</li>
          </ul>

          <div className={styles.warningBox}>
            <p className={styles.warningTitle}>This can't be undone</p>
            <p className={styles.warningBody}>
              Posts and comments you've contributed to a community may be retained as part of
              that community's records. If you need those removed too, email{' '}
              <a className={styles.contactLink} href="mailto:developers@joyya.com">
                developers@joyya.com
              </a>{' '}
              and we'll handle it for you.
            </p>
          </div>

          <h2 className={styles.sectionTitle}>Confirm it's you</h2>
          <Form onSubmit={handleSubmit} error={!!error}>
            <div className={formStyles.field}>
              <label className={styles.fieldLabel}>Email</label>
              <Input
                fluid
                type="email"
                value={userName}
                placeholder="The email you sign in with"
                disabled={busy}
                onChange={(e) => {
                  setUserName(e.target.value);
                  setError(null);
                }}
              />
            </div>

            <div className={formStyles.field}>
              <label className={styles.fieldLabel}>Password</label>
              <Input
                fluid
                type="password"
                value={password}
                disabled={busy}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                }}
              />
            </div>

            <div className={formStyles.field}>
              <label className={styles.fieldLabel}>
                Type DELETE to confirm you understand this permanently deletes your account
                and can't be undone.
              </label>
              <Input
                fluid
                value={confirmText}
                placeholder="DELETE"
                disabled={busy}
                onChange={(e) => {
                  setConfirmText(e.target.value);
                  setError(null);
                }}
              />
            </div>

            {error && <p className={formStyles.errorMsg}>{error}</p>}

            <Button
              type="submit"
              fluid
              size="large"
              className={styles.deleteBtn}
              disabled={!canSubmit}
              loading={busy}
            >
              Delete my account
            </Button>
          </Form>

          <p className={styles.footer}>
            Changed your mind?{' '}
            <Link className={styles.contactLink} to={PATHS.LOGIN}>
              Go back to sign in
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
};
